import { useState } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { FaTrash, FaEdit } from 'react-icons/fa';
import {
  Button,
  Descriptions,
  Image,
  Modal,
  Popover,
  Skeleton,
  Space,
  Timeline,
  Typography,
} from 'antd';
import { WorkOrderObservation } from '@/core/domain/workorder/WorkOrdersObservations/WorkOrdersObservations.types';
import { useDeleteWorkOrderObservation } from '@/core/domain/workorder/WorkOrdersObservations/WorkOrdersObservations.hooks';
import { useDisclosure, useRouteMatchParam } from '@/hooks';
import WorkOrderObservationModal from '../ObservationModal';
import { FileImageOutlined, FilePdfOutlined } from '@ant-design/icons';
import { Group } from '@/components/_common';
import isImageFileUrl from '@/utils/functions/isImageFileUrl';
import getFileNameFromUrl from '@/utils/functions/getFileNameFromUrl';
import isPdfFileUrl from '@/utils/functions/isPdfFileUrl';

const { Text, Paragraph, Title } = Typography;

interface ObservationsTimelineProps {
  isLoading: boolean;
  observations?: WorkOrderObservation[];
}

function ObservationsTimeline({
  observations,
  isLoading,
}: ObservationsTimelineProps) {
  const intl = useIntl();
  const [observationSelected, setObservationSelected] =
    useState<WorkOrderObservation>();
  const [modalVisibel, modalAction] = useDisclosure(false, {
    onClose() {
      setObservationSelected(undefined);
    },
  });
  const [previewingImageUrl, setPreviewingImageUrl] = useState<
    string | undefined
  >();

  const removeObservation = useDeleteWorkOrderObservation();
  const complementId = useRouteMatchParam('complementId');

  function removeConfirm(observationId: number) {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'workOrders.details.observations.remove.confirm.title',
      }),
      content: intl.formatMessage({
        id: 'workOrders.details.observations.remove.confirm.content',
      }),
      okText: 'Remover',
      onOk: () =>
        removeObservation.mutateAsync({
          observationId,
          workOrderComplementId: Number(complementId),
        }),
    });
  }

  const getFileIcon = (fileName: string) => {
    if (isPdfFileUrl(fileName))
      return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />;
    return <FileImageOutlined style={{ fontSize: 16 }} />;
  };

  return (
    <>
      <Skeleton active loading={isLoading}>
        <Timeline
          style={{ maxHeight: '314px', overflowY: 'auto', paddingTop: '12px' }}
        >
          {observations?.map((item) => (
            <Timeline.Item key={item.id}>
              <Title level={4} style={{ marginBottom: 0 }}>
                {item.observation}
              </Title>

              <Space align="center">
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  <Text style={{ fontSize: '12px' }}>
                    {item.employeeName} -{' '}
                    {moment(item.createdAt).format('DD/MM/YYYY HH:mm')}
                  </Text>
                </Paragraph>
                <Button
                  size="small"
                  type="text"
                  icon={<FaEdit />}
                  onClick={() => {
                    setObservationSelected(item);
                    modalAction.open();
                  }}
                />
                <Button
                  size="small"
                  danger
                  type="text"
                  icon={<FaTrash />}
                  onClick={() => removeConfirm(item.id)}
                />
              </Space>

              {item?.attachments && (
                <div style={{ marginTop: 6 }}>
                  <Popover
                    content={
                      isImageFileUrl(item.attachments)
                        ? 'Visualizar Imagem'
                        : 'Visualizar Arquivo'
                    }
                  >
                    <Button
                      type="default"
                      style={{ paddingRight: 8, paddingLeft: 8 }}
                      onClick={() => {
                        if (isImageFileUrl(item.attachments)) {
                          setPreviewingImageUrl(item?.attachments);
                        } else {
                          window.open(item.attachments, '_blank');
                        }
                      }}
                    >
                      <Group
                        style={{
                          maxWidth: 150,
                          overflow: 'hidden',
                          msTextOverflow: 'ellipsis',
                        }}
                      >
                        {getFileIcon(item.attachments)}
                        {getFileNameFromUrl(item.attachments)}
                      </Group>
                    </Button>
                  </Popover>

                  {isImageFileUrl(item.attachments) && (
                    <Image
                      style={{ display: 'none' }}
                      src={item.attachments}
                      preview={{
                        visible: previewingImageUrl === item.attachments,
                        src: item.attachments,
                        onVisibleChange: (visible) => {
                          if (!visible) {
                            setPreviewingImageUrl(undefined);
                          }
                        },
                      }}
                    />
                  )}
                </div>
              )}
            </Timeline.Item>
          ))}

          {!observations?.length ? (
            <Descriptions bordered>
              <Descriptions.Item>
                {intl.formatMessage({
                  id: 'workOrders.details.observations.empty',
                })}
              </Descriptions.Item>
            </Descriptions>
          ) : null}
        </Timeline>
      </Skeleton>

      <WorkOrderObservationModal
        visible={modalVisibel}
        handleVisible={modalAction.close}
        observation={observationSelected}
      />
    </>
  );
}

export default ObservationsTimeline;
